import React, { useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Route, Switch } from 'react-router-dom';
import { Provider as GraphQLProvider } from './apollo';
import { useGetAuthenticationOptionsQuery } from './__generated__/graphql';
import {
  getCachedLoginImageUrl,
  setCachedLoginImageUrl,
} from './loginImageCache';
import { routes } from './routes';

const Routes: React.FC<React.PropsWithChildren<unknown>> = () => (
  <Switch>
    {Object.values(routes).map(({ component, route }, i) => (
      <Route
        key={i}
        exact={route.template() === '/'}
        path={route.template()}
        component={component}
      />
    ))}
  </Switch>
);

const defaultImgUrl = new URL('/signin-cover.jpg', import.meta.url).href;

const AppLayout: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { data } = useGetAuthenticationOptionsQuery();
  const serverUrl =
    data?.getAuthenticationOptions?.organization?.loginImageUrl ?? null;
  const cachedUrl = getCachedLoginImageUrl();
  const imgUrl = serverUrl || cachedUrl || defaultImgUrl;

  useEffect(() => {
    if (!data) return;
    setCachedLoginImageUrl(serverUrl);
  }, [data, serverUrl]);

  return (
    <>
      <link rel="preload" as="image" href={imgUrl} />
      <div className="d-flex align-items-center bg-auth border-top border-top-2 border-primary overflow-hidden">
        <Container fluid>
          <Row className="align-items-center justify-content-center">
            <Col md={5} lg={6} xl={4} className="px-lg-6 my-5">
              <Routes />
            </Col>
            <Col md={7} lg={6} xl={8} className="d-none d-lg-block">
              <div
                className="vh-100 mt-n1 me-n3"
                data-testid="login-image-panel"
                style={{
                  backgroundImage: `url(${imgUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#faf8f7',
                }}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

const App: React.FC<React.PropsWithChildren<unknown>> = () => (
  <GraphQLProvider>
    <AppLayout />
  </GraphQLProvider>
);

export default App;
